import {
  Box,
  Dialog,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateKey } from "../i18n/translate";
import { usePremisesList } from "../hooks/usePremisesList";
import { usePageAccess, type PageAccess } from "../auth/access";
import { PAGE_ACCESS } from "../nav/pageAccess";

export interface Command {
  id: string;
  /** English; the shown label is `shell:palette.commands.<id>`. */
  label: string;
  group: "goTo" | "premises";
  href: string;
  keywords?: string;
  /** The permission of the page, as its menu item names it. */
  access?: PageAccess;
}

export const NAV_COMMANDS: Command[] = [
  {
    id: "nav-premises",
    label: "Premises & Screens",
    group: "goTo",
    href: "/premises",
  },
  {
    id: "nav-companies",
    label: "Companies",
    group: "goTo",
    href: "/companies",
    access: PAGE_ACCESS.companies,
  },
  {
    id: "nav-analytics",
    label: "Overview · Fleet health",
    group: "goTo",
    href: "/analytics",
    access: PAGE_ACCESS.fleetHealth,
    keywords: "fleet devices analytics uptime",
  },
  {
    id: "nav-health-alerts",
    label: "Overview · Health alerting",
    group: "goTo",
    href: "/health-alerts",
    access: PAGE_ACCESS.healthAlerts,
    keywords: "alerts rules offline notify email",
  },
  {
    id: "nav-media",
    label: "Global media",
    group: "goTo",
    href: "/media",
    access: PAGE_ACCESS.globalMedia,
    keywords: "media library assets images logos catalogue",
  },
  {
    id: "nav-content",
    label: "Content",
    group: "goTo",
    href: "/frontlayer/media",
    access: PAGE_ACCESS.media,
    keywords: "media library assets images customer company premise innehall",
  },
  {
    id: "nav-home-screen",
    label: "Home screen",
    group: "goTo",
    href: "/frontlayer/home",
    access: PAGE_ACCESS.tvLayout,
    keywords:
      "start navigation startvy menu tiles screens apps radio information",
  },
  {
    id: "nav-appearance",
    label: "Appearance",
    group: "goTo",
    href: "/frontlayer/appearance",
    access: PAGE_ACCESS.tvAppearance,
    keywords: "template utseende colours typography background branding logo",
  },
  {
    id: "nav-users",
    label: "Users",
    group: "goTo",
    href: "/users",
    access: PAGE_ACCESS.users,
  },
  {
    id: "nav-settings",
    label: "General settings",
    group: "goTo",
    href: "/settings/general",
    access: PAGE_ACCESS.generalSettings,
  },
  {
    id: "nav-room-types",
    label: "Unit types",
    group: "goTo",
    href: "/room-types",
    access: PAGE_ACCESS.roomTypes,
  },
  {
    id: "nav-templates",
    label: "Templates",
    group: "goTo",
    href: "/templates",
    access: PAGE_ACCESS.templateDefaults,
  },
  {
    id: "nav-apps",
    label: "Apps",
    group: "goTo",
    href: "/apps",
    access: PAGE_ACCESS.appCatalogue,
  },
  {
    id: "nav-radio",
    label: "Radio",
    group: "goTo",
    href: "/radio-stations",
    access: PAGE_ACCESS.radioStations,
  },
  {
    id: "nav-distribution-channels",
    label: "Distribution · Channels",
    group: "goTo",
    href: "/distribution/channels",
    access: PAGE_ACCESS.distribution,
    // Both old names stay searchable: an operator who learned "effect
    // channels" should still land here after the two were merged.
    keywords:
      "channels effect linear live loops autoplayer broadcast lineup streams iptv",
    // Same gate the nav and every Distribution route use. The palette is the
    // second front door, so it must move with them: gated by the permission
    // rather than by staff-ness, or a key-holder finds the menu hidden here
    // while the server would have let them in.
  },
  {
    id: "nav-distribution-lineup",
    label: "Distribution · Line-up",
    group: "goTo",
    href: "/distribution/lineup",
    access: PAGE_ACCESS.distribution,
    // "channel order" and "channel numbers" are what an operator will actually
    // type, and neither word is in the label. `numbers` is deliberate even
    // though the model emits none: it is what somebody looking for this asks
    // for, and landing them here is how they learn there are none.
    keywords:
      "lineup line-up channel order channel numbers sort ordering national svt tv4",
  },
  {
    id: "nav-distribution-vod",
    label: "Distribution · VOD",
    group: "goTo",
    href: "/distribution/vod",
    access: PAGE_ACCESS.distribution,
    keywords: "vod video on demand titles movies shelf",
  },
  {
    id: "nav-distribution-carousels",
    label: "Distribution · Carousels",
    group: "goTo",
    href: "/distribution/carousels",
    access: PAGE_ACCESS.distribution,
    keywords:
      "carousels rails rows home page shelf curated dynamic collection epg karusell",
  },
  {
    id: "nav-distribution-products",
    label: "Distribution · Products",
    group: "goTo",
    href: "/distribution/products",
    access: PAGE_ACCESS.distribution,
    keywords:
      "products offering feature set featureset price sold sell package grant entitlement upsales produkt erbjudande",
  },
  {
    id: "nav-distribution-packages",
    label: "Distribution · Packages",
    group: "goTo",
    href: "/distribution/packages",
    access: PAGE_ACCESS.distribution,
    keywords:
      "packages entitlement media collection rule channels curated pin paket",
  },
  {
    id: "nav-distribution-providers",
    label: "Distribution · Content feeds",
    group: "goTo",
    href: "/distribution/providers",
    access: PAGE_ACCESS.distribution,
    keywords:
      "content feeds providers suppliers mrss trusted licence rule leverantor distributor scanbox",
  },
  {
    id: "nav-distribution-submissions",
    label: "Distribution · Review queue",
    group: "goTo",
    href: "/distribution/submissions",
    access: PAGE_ACCESS.distribution,
    keywords:
      "review queue submissions approve reject screening supplier upload granska",
  },
  {
    id: "nav-distribution-bulk-ingest",
    label: "Distribution · Bulk ingest",
    group: "goTo",
    href: "/distribution/bulk-ingest",
    access: PAGE_ACCESS.distribution,
    keywords: "bulk ingest upload jobs transcode package",
  },
  {
    id: "nav-distribution-order-support",
    label: "Distribution · Order support",
    group: "goTo",
    href: "/distribution/order-support",
    access: PAGE_ACCESS.distribution,
    keywords:
      "order support refund revoke receipt purchase portal tvod rental customer kundtjänst",
  },
  {
    id: "nav-distribution-portal-customers",
    label: "Distribution · Portal customers",
    group: "goTo",
    href: "/distribution/portal-customers",
    access: PAGE_ACCESS.distribution,
    keywords:
      "portal customers register consumer accounts tvod gdpr erase export lock kunder",
  },
  {
    id: "nav-distribution-plan-reports",
    label: "Distribution · Plan reports",
    group: "goTo",
    href: "/distribution/plan-reports",
    access: PAGE_ACCESS.distribution,
    keywords:
      "plan reports settlement studios picks organisation sales split rapport",
  },
  {
    id: "nav-distribution-upsales",
    label: "Distribution · Upsales",
    group: "goTo",
    href: "/distribution/upsales",
    access: PAGE_ACCESS.distribution,
    keywords:
      "upsales crm sync dry run subscription agreement customer organisation plans",
  },
  {
    id: "nav-distribution-ingest",
    label: "Distribution · Ingest status",
    group: "goTo",
    href: "/distribution/ingest",
    access: PAGE_ACCESS.distribution,
    keywords:
      "ingest status overview running queued failed transcode videocore mrss sync stack capacity",
  },
];

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);
export const PALETTE_EVENT = "foyer:command-palette";
export const PALETTE_HINT = isMac ? "⌘K" : "Ctrl K";

/**
 * Global ⌘K / Ctrl-K command palette: fuzzy-jump to any section or premise.
 * Opens on the hotkey or a `foyer:command-palette` window event (dispatched by
 * the sidebar trigger).
 */
export default function CommandPalette() {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const { premises } = usePremisesList();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    function onEvent() {
      setOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener(PALETTE_EVENT, onEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(PALETTE_EVENT, onEvent);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  const mayOpen = usePageAccess();
  const commands = useMemo<Command[]>(() => {
    const prem: Command[] = premises.map((p) => ({
      id: `prem-${p.premiseId}`,
      label: p.premiseName,
      group: "premises",
      href: `/premises/${p.premiseId}`,
      keywords: p.premiseId,
    }));
    const nav = NAV_COMMANDS.filter((c) => !c.access || mayOpen(c.access)).map(
      (c) => ({
        ...c,
        label: translateKey(t, `shell:palette.commands.${c.id}`),
        keywords: `${c.label} ${c.keywords ?? ""}`,
      })
    );
    return [...nav, ...prem];
  }, [premises, mayOpen, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.keywords ?? ""} ${t(`palette.groups.${c.group}`)}`
        .toLowerCase()
        .includes(q)
    );
  }, [commands, query, t]);

  useEffect(() => setActive(0), [query]);

  function run(cmd: Command | undefined) {
    if (!cmd) return;
    navigate(cmd.href);
    setOpen(false);
  }

  function onInputKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(filtered[active]);
    }
  }

  // Keep the active row in view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${active}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  let lastGroup = "";

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { position: "fixed", top: 80, m: 0 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <SearchIcon sx={{ color: "text.secondary" }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder={t("palette.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKey}
          sx={{ typography: "titleSm" }}
          inputProps={{ "data-testid": "command-palette-input" }}
        />
      </Box>
      <List ref={listRef} sx={{ maxHeight: 360, overflowY: "auto", py: 1 }}>
        {filtered.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("palette.noMatches")}
            </Typography>
          </Box>
        )}
        {filtered.map((cmd, i) => {
          const showGroup = cmd.group !== lastGroup;
          lastGroup = cmd.group;
          return (
            <Box key={cmd.id}>
              {showGroup && (
                <Typography
                  variant="overline"
                  sx={{ display: "block", px: 2, pt: 1 }}
                >
                  {t(`palette.groups.${cmd.group}`)}
                </Typography>
              )}
              <ListItemButton
                data-index={i}
                selected={i === active}
                onClick={() => run(cmd)}
                onMouseEnter={() => setActive(i)}
                sx={{ mx: 1, borderRadius: 2 }}
              >
                <ListItemText primary={cmd.label} />
              </ListItemButton>
            </Box>
          );
        })}
      </List>
    </Dialog>
  );
}
